import * as React from "react";

import * as ProgressPrimitive from "@radix-ui/react-progress";

import { cn } from "~/lib/utils";

interface CreditProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  value?: number;
  className?: string;
}

const CreditProgress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  CreditProgressProps
>(({ className, value, ...props }, ref) => {
  // Ensure the value is within range 0-100
  const normalizedValue = Math.min(Math.max(0, value || 0), 100);

  // Determine color based on credit utilization - opposite of normal progress
  // Low utilization = good (green), high utilization = danger (red)
  const getColorClass = () => {
    if (normalizedValue >= 90) return "bg-red-500"; // Critical: 90%+ utilization
    if (normalizedValue >= 75) return "bg-orange-500"; // Warning: 75%+ utilization
    if (normalizedValue >= 50) return "bg-amber-500"; // Caution: 50%+ utilization
    return "bg-green-500"; // Good: <50% utilization
  };

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
        className
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={cn("h-full w-full flex-1 transition-all", getColorClass())}
        style={{ transform: `translateX(-${100 - normalizedValue}%)` }}
      />
    </ProgressPrimitive.Root>
  );
});
CreditProgress.displayName = "CreditProgress";

export { CreditProgress };
